import type { ISocialOffer } from "@bbj-types/offer";
import type { ObjectId } from "@bbj-types/utils";
import {
  isOnUpdateOfferWithFavorite,
  isSocialOfferWithFavorite,
} from "@candidate/details/views/socialOffer/logics/types";
import { useGlobalModalConfirmation } from "@complex/GlobalModalConfirmation";
import type { IIcon, IIconsGroup } from "@data-display/IconsGroup";
import { NewReleases } from "@mui/icons-material";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { OfferKind } from "@root/utils/enums/offer";
import { useModalSocialOfferContext } from "@styleguide";
import { useMemo, useState } from "react";
import type { IDrawerSocialOfferHeaderProps } from ".";
import { useDrawerSocialOfferActions } from "./logics";

export const DrawerSocialOfferActions = <
  T extends ISocialOffer & { spotlightDates?: { startDate: Date; endDate: Date }; spotlightId?: ObjectId },
>(
  socialOffer: T,
  onClose: IDrawerSocialOfferHeaderProps<T>["onClose"],
  expired: boolean,
  handleRemoveOffer?: IDrawerSocialOfferHeaderProps<T>["handleRemoveOffer"],
  handleRemoveSocialOfferSpotlight?: IDrawerSocialOfferHeaderProps<T>["handleRemoveSocialOfferSpotlight"],
  onUpdateOffer?: IDrawerSocialOfferHeaderProps<T>["onUpdateOffer"],
  openEditModal?: IDrawerSocialOfferHeaderProps<T>["openEditModal"],
  isApply?: boolean
) => {
  const confirm = useGlobalModalConfirmation();
  const {
    archiveOffer,
    isAuthorizedAddFavorite,
    isAuthorizedAddAndRemoveSpotlight,
    isAuthorizedModifyAndCreate,
    isAuthorizedArchive,
    handleClickAddFavorite,
    handleClickRemoveFavorite,
    handleClickRemoveSpotlight,
  } = useDrawerSocialOfferActions(socialOffer, onUpdateOffer);
  const openModalSocialOffer = useModalSocialOfferContext();
  const [addSpotlightModal, setAddSpotlightModal] = useState<boolean>(false);
  const onConfirmArchiveModal = async () => {
    if (
      await confirm(
        <Typography variant="h4">Archiver une offre</Typography>,
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography>
            Vous êtes sur le point d’archiver cette offre. Vous ne la verrez plus apparaître dans les résultats de
            recherche. Cette action est irrévocable.
          </Typography>
          <Typography>C’est votre dernier mot ?</Typography>
        </Stack>,
        { confirmLabel: "Valider" }
      )
    ) {
      try {
        await archiveOffer(OfferKind.SOCIAL, socialOffer.offerId);
        if (handleRemoveOffer) {
          handleRemoveOffer(socialOffer);
        }
        window.publish("feedback", {
          severity: "success",
          message: "L'offre de service a été archivée",
        });
      } catch {
        window.publish("feedback", {
          severity: "error",
          message: "Une erreur est survenue lors de l'archivage de l'offre.",
        });
      }
      onClose();
    }
  };

  const onConfirmRemoveSocialOfferSpotlightModal = async () => {
    if (
      await confirm(
        <Typography variant="h4">Retirer une offre de la une </Typography>,
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography>
            Vous êtes sur le point de retirer cette offre de la une. Elle n'apparaîtra plus par défaut sur la page des
            offres de service ou dans l'onglet "Offres de service" d'une fiche candidat. Elle apparaîtra cependant
            toujours dans les résultats de recherche.
          </Typography>
          <Typography>C'est votre dernier mot ?</Typography>
        </Stack>,
        { confirmLabel: "Valider" }
      )
    ) {
      if (socialOffer.spotlightId) {
        const res = await handleClickRemoveSpotlight(socialOffer.spotlightId);
        if (res.status === 204) {
          handleRemoveSocialOfferSpotlight?.(socialOffer);
          onUpdateOffer?.({ ...socialOffer, spotlightDates: undefined, spotlightId: undefined });
          onClose();
          window.publish("feedback", {
            severity: "success",
            message: "L'offre a bien été retirée de la liste des offres à la une",
          });
        }
      }
    }
  };

  const { icons, limit }: IIconsGroup = useMemo(() => {
    const icons: IIcon[] = [];
    const limit = 2;

    const archive =
      handleRemoveOffer && isAuthorizedArchive
        ? {
            label: "Archiver",
            component: (
              <IconButton>
                <ArchiveOutlinedIcon color="primary" />
              </IconButton>
            ),
            callBack: () => onConfirmArchiveModal(),
            hidden: false,
          }
        : null;

    const duplicate =
      isAuthorizedModifyAndCreate &&
      openEditModal &&
      isSocialOfferWithFavorite(socialOffer) &&
      isOnUpdateOfferWithFavorite(socialOffer, onUpdateOffer)
        ? {
            label: "Dupliquer",
            component: (
              <IconButton>
                <FileCopyOutlinedIcon color="primary" />
              </IconButton>
            ),
            callBack: () => openModalSocialOffer(socialOffer, onUpdateOffer, true),
            hidden: false,
          }
        : null;

    const modify =
      isAuthorizedModifyAndCreate &&
      openEditModal &&
      isSocialOfferWithFavorite(socialOffer) &&
      isOnUpdateOfferWithFavorite(socialOffer, onUpdateOffer) &&
      !expired
        ? {
            label: "Modifier",
            component: (
              <IconButton>
                <EditOutlinedIcon color="primary" />
              </IconButton>
            ),
            callBack: () => openModalSocialOffer(socialOffer, onUpdateOffer, false),
            hidden: false,
          }
        : null;

    const star =
      isAuthorizedAddFavorite &&
      isSocialOfferWithFavorite(socialOffer) &&
      isOnUpdateOfferWithFavorite(socialOffer, onUpdateOffer)
        ? socialOffer.favoriteChallenges.length > 0
          ? {
              label: "Enlever des favoris",
              component: (
                <IconButton>
                  <Tooltip title="Enlever des favoris">
                    <StarIcon color="yellow" />
                  </Tooltip>
                </IconButton>
              ),
              callBack: () => handleClickRemoveFavorite(),
              hidden: false,
            }
          : !expired
            ? {
                label: "Mettre en favori",
                component: (
                  <IconButton>
                    <Tooltip title="Mettre en favori">
                      <StarBorderOutlinedIcon />
                    </Tooltip>
                  </IconButton>
                ),
                callBack: () => handleClickAddFavorite(),
                hidden: false,
              }
            : null
        : null;

    const spotlightRemove =
      isAuthorizedAddAndRemoveSpotlight && socialOffer.spotlightDates && !expired && !isApply
        ? {
            label: "Retirer de la une",
            component: (
              <IconButton>
                <Tooltip title="Retirer de la une">
                  <NewReleases color="yellow" />
                </Tooltip>
              </IconButton>
            ),
            callBack: () => onConfirmRemoveSocialOfferSpotlightModal(),
            hidden: false,
          }
        : null;

    const spotlightAdd =
      isAuthorizedAddAndRemoveSpotlight && !socialOffer.spotlightDates && !expired && !isApply
        ? {
            label: "Mettre à la une",
            component: (
              <IconButton>
                <Tooltip title="Mettre à la une">
                  <NewReleasesOutlinedIcon color="primary" />
                </Tooltip>
              </IconButton>
            ),
            callBack: () => {
              setAddSpotlightModal(true);
            },
            hidden: false,
          }
        : null;

    if (socialOffer.organisation) {
      if (modify) {
        icons.push(modify);
      }
      if (star && isAuthorizedAddFavorite) {
        icons.push(star);
      } else if (
        !isAuthorizedAddFavorite &&
        isSocialOfferWithFavorite(socialOffer) &&
        socialOffer.favoriteChallenges.length > 0
      ) {
        icons.push({
          label: "Favorite",
          component: (
            <Stack
              sx={{
                mx: "8px",
              }}
            >
              <StarIcon color="yellow" />
            </Stack>
          ),
          hidden: false,
        });
      }
      if (duplicate) {
        icons.push(duplicate);
      }
      if (spotlightRemove) {
        icons.push(spotlightRemove);
      }
      if (spotlightAdd) {
        icons.push(spotlightAdd);
      }
      if (archive) {
        icons.push(archive);
      }
    } else {
      if (star) {
        icons.push(star);
      } else if (isSocialOfferWithFavorite(socialOffer) && socialOffer.favoriteChallenges.length > 0) {
        icons.push({
          label: "Favorite",
          component: (
            <Stack
              sx={{
                mx: "8px",
              }}
            >
              <StarIcon color="yellow" />
            </Stack>
          ),
          hidden: false,
        });
      }
      if (spotlightRemove) {
        icons.push(spotlightRemove);
      }
      if (spotlightAdd) {
        icons.push(spotlightAdd);
      }
    }

    return { icons, limit };
  }, [
    isAuthorizedAddFavorite,
    isAuthorizedAddAndRemoveSpotlight,
    isAuthorizedArchive,
    socialOffer,
    expired,
    isApply,
    handleClickAddFavorite,
    handleClickRemoveFavorite,
    handleRemoveOffer,
    onConfirmArchiveModal,
    onConfirmRemoveSocialOfferSpotlightModal,
    onUpdateOffer,
    openEditModal,
    openModalSocialOffer,
  ]);

  return {
    icons,
    limit,
    addSpotlightModal,
    setAddSpotlightModal,
  };
};
